import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import {
  createRecorder,
  templatePath,
  type AudioHandle,
  type FinishedRecording,
  type Recorder,
  type StartOutcome,
} from '@financialharmony/show-and-tell';
import { mintClientSubmissionId, sanitizeFeedbackContext } from '@financialharmony/ui/feedback/core';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { useFeedbackContextApi } from '@/contexts/useFeedbackContext';
import { startCaptures } from '@/lib/showAndTell/startCaptures';
import { getFeedbackSentryIds } from '@/lib/feedback/feedbackSentryIds';
import { checkShowAndTellSupport, detectShowAndTellSupport, type Support } from '@/lib/showAndTell/capability';
import { createMicrophoneSource } from '@/lib/showAndTell/microphoneSource';
import { uploadWithProgress } from '@/lib/showAndTell/xhrUploadTransport';
import {
  ShowAndTellContext,
  type ShowAndTellApi,
  type ShowAndTellFailure,
  type ShowAndTellStatus,
} from './showAndTellStore';

/** Overridable collaborators; tests inject fakes, the app uses the defaults. */
export interface ShowAndTellDeps {
  audioSource?: () => Promise<AudioHandle>;
  now?: () => number;
  upload?: typeof uploadWithProgress;
  maxMs?: number;
  tickMs?: number;
  enabled?: boolean;
}

const DEFAULT_MAX_MS = Number(import.meta.env.VITE_SHOW_TELL_MAX_SECONDS ?? 600) * 1000;
const DEFAULT_TICK_MS = 250;

function toFailure(error: unknown): ShowAndTellFailure {
  // Only backend refusals carry a sentence written for the user; anything else gets the generic one.
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code === 'string') {
    const { message, reference } = error as { message?: string; reference?: string };
    return { message: message || undefined, reference };
  }
  return {};
}

export function ShowAndTellProvider({ children, deps }: { children: ReactNode; deps?: ShowAndTellDeps }) {
  const location = useLocation();
  const t = useTranslations('consultantShowAndTell.control');
  const feedbackContext = useFeedbackContextApi();

  const enabledDep = deps?.enabled;
  const support: Support = useMemo(
    () =>
      enabledDep === undefined
        ? detectShowAndTellSupport()
        : checkShowAndTellSupport({ enabled: enabledDep, hasGetUserMedia: true, hasMediaRecorder: true, isMobile: false }),
    [enabledDep],
  );

  const [status, setStatus] = useState<ShowAndTellStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [failure, setFailure] = useState<ShowAndTellFailure | null>(null);

  const recorderRef = useRef<Recorder | null>(null);
  const finishedRef = useRef<FinishedRecording | null>(null);
  const stopUiEventsRef = useRef<(() => void) | null>(null);
  const noteRef = useRef('');
  const submissionIdRef = useRef('');
  const contextRef = useRef<Record<string, string>>({});
  const routeRef = useRef(templatePath(location.pathname));
  const sendingRef = useRef(false);

  const maxMs = deps?.maxMs ?? DEFAULT_MAX_MS;
  const tickMs = deps?.tickMs ?? DEFAULT_TICK_MS;
  const audioSourceDep = deps?.audioSource;
  const nowDep = deps?.now;
  const uploadDep = deps?.upload;

  const reset = useCallback(() => {
    stopUiEventsRef.current?.();
    stopUiEventsRef.current = null;
    recorderRef.current = null;
    finishedRef.current = null;
    sendingRef.current = false;
    setStatus('idle');
    setElapsedMs(0);
    setUploadPercent(0);
    setFailure(null);
  }, []);

  // Route changes become steps while recording; the route itself is always a template (EC22).
  useEffect(() => {
    const next = templatePath(location.pathname);
    if (next === routeRef.current) return;
    routeRef.current = next;
    recorderRef.current?.log({ type: 'step', name: next, role: 'navigation' });
  }, [location.pathname]);

  // The recorder detects cap and mic loss on this tick; the UI mirrors its state.
  useEffect(() => {
    if (status !== 'recording' && status !== 'paused') return;
    const id = window.setInterval(() => {
      const recorder = recorderRef.current;
      if (!recorder) return;
      recorder.checkLimits();
      setElapsedMs(recorder.elapsedMs());
      if (recorder.state === 'capped' || recorder.state === 'mic-lost') setStatus(recorder.state);
    }, tickMs);
    return () => window.clearInterval(id);
  }, [status, tickMs]);

  // A reload or tab close loses the recording in v0, so the browser asks first (EC15).
  useEffect(() => {
    if (status === 'idle') return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [status]);

  useEffect(
    () => () => {
      recorderRef.current?.cancel();
      stopUiEventsRef.current?.();
    },
    [],
  );

  const start = useCallback(
    async (note: string): Promise<StartOutcome | 'unsupported'> => {
      if (!support.available) return 'unsupported';
      if (recorderRef.current) return 'already-active';
      const recorder = createRecorder({
        audioSource: audioSourceDep ?? createMicrophoneSource(),
        now: nowDep ?? (() => performance.now()),
        route: () => routeRef.current,
        limits: { maxMs },
        release: import.meta.env.VITE_RELEASE,
        viewport: () => ({ w: window.innerWidth, h: window.innerHeight }),
      });
      const outcome = await recorder.start();
      if (outcome !== 'started') return outcome;
      recorderRef.current = recorder;
      noteRef.current = note;
      submissionIdRef.current = mintClientSubmissionId();
      contextRef.current = {
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        locale: navigator.language,
        ...sanitizeFeedbackContext(feedbackContext?.getContext() ?? {}),
      };
      stopUiEventsRef.current = startCaptures(recorder.log);
      setElapsedMs(0);
      setFailure(null);
      setStatus('recording');
      return 'started';
    },
    [support, audioSourceDep, nowDep, maxMs, feedbackContext],
  );

  const pause = useCallback(() => {
    recorderRef.current?.pause();
    if (recorderRef.current?.state === 'paused') setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    recorderRef.current?.resume();
    if (recorderRef.current?.state === 'recording') setStatus('recording');
  }, []);

  const runUpload = useCallback(async () => {
    const finished = finishedRef.current;
    if (!finished) return;
    setStatus('uploading');
    setUploadPercent(0);
    setFailure(null);
    try {
      const service = ServiceRegistry.getFeedbackService();
      const created = await service.createRecordingUpload({
        clientSubmissionId: submissionIdRef.current,
        mimeType: finished.audio.mimeType,
        bytes: finished.audio.bytes,
      });
      // uploadUrl is null when the audio already landed on an earlier attempt.
      if (created.uploadUrl) {
        await (uploadDep ?? uploadWithProgress)({
          url: created.uploadUrl,
          blob: finished.audio.data as Blob,
          // Storage matches the bare type, not "audio/webm;codecs=opus".
          contentType: finished.audio.mimeType.split(';')[0],
          onProgress: setUploadPercent,
        });
      }
      const sentryIds = await getFeedbackSentryIds();
      await service.finalizeRecording({
        recordingId: created.recordingId,
        clientSubmissionId: submissionIdRef.current,
        message: noteRef.current,
        route: finished.bundle.route,
        context: contextRef.current,
        sentryEventId: sentryIds.sentryEventId ?? undefined,
        sentryReplayId: sentryIds.sentryReplayId ?? undefined,
        bundle: finished.bundle,
      });
      toast.success(t('sent'));
      reset();
    } catch (error) {
      setFailure(toFailure(error));
      setStatus('upload-failed');
      sendingRef.current = false;
    }
  }, [uploadDep, reset, t]);

  const send = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || sendingRef.current) return;
    sendingRef.current = true;
    stopUiEventsRef.current?.();
    stopUiEventsRef.current = null;
    try {
      finishedRef.current = await recorder.finish();
    } catch (error) {
      setFailure(toFailure(error));
      setStatus('upload-failed');
      sendingRef.current = false;
      return;
    }
    await runUpload();
  }, [runUpload]);

  const retryUpload = useCallback(async () => {
    if (sendingRef.current || !finishedRef.current) return;
    sendingRef.current = true;
    await runUpload();
  }, [runUpload]);

  const cancel = useCallback(() => {
    recorderRef.current?.cancel();
    reset();
  }, [reset]);

  const discard = useCallback(() => {
    recorderRef.current?.cancel();
    reset();
  }, [reset]);

  const api: ShowAndTellApi = useMemo(
    () => ({ support, status, elapsedMs, uploadPercent, failure, start, pause, resume, send, retryUpload, cancel, discard }),
    [support, status, elapsedMs, uploadPercent, failure, start, pause, resume, send, retryUpload, cancel, discard],
  );

  return <ShowAndTellContext.Provider value={api}>{children}</ShowAndTellContext.Provider>;
}
